import React, { useState, useEffect } from 'react';
import { FormattedNumber, FormattedMessage } from 'react-intl';

import Close from 'components/Shared/Icons/Close';
import InformationIcon from 'components/Shared/Icons/InformationIcon';
import { colors, fontSize } from 'styles/cp';
import { UnpaidInvoice } from 'types/Insight';

interface BatchInvoicePageMessageProps {
  batchInvoices: UnpaidInvoice[];
  maxSelectableBalance: number;
  currency: string;
}

const BatchInvoicePageMessage = ({
  batchInvoices,
  maxSelectableBalance,
  currency,
}: BatchInvoicePageMessageProps) => {
  const [showPageMessage, setShowPageMessage] = useState(true);

  // Check if there are any non-default invoices that are disabled due to amount limit
  const hasDisabledNonDefaultInvoices =
    batchInvoices?.some(
      (invoice) => !invoice.defaultInvoice && invoice.balance > maxSelectableBalance
    ) || false;

  useEffect(() => {
    // Check if sessionStorage is available (not in test environment)
    if (typeof window !== 'undefined' && window.sessionStorage) {
      const isDismissed = window.sessionStorage.getItem('batchInvoiceLimitMessage');
      if (isDismissed === 'true') {
        setShowPageMessage(false);
      }
    }
  }, []);

  const handleClose = () => {
    setShowPageMessage(false);
    // Check if sessionStorage is available (not in test environment)
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.setItem('batchInvoiceLimitMessage', 'true');
    }
  };

  // Don't show if dismissed or if there are no disabled non-default invoices
  if (!showPageMessage || !hasDisabledNonDefaultInvoices) {
    return null;
  }

  return (
    <React.Fragment>
      <div className="batch-invoice-pagemessage-wrapper">
        <div className="batch-invoice-pagemessage-container">
          <div className="batch-invoice-pagemessage-body">
            <InformationIcon />
            <div className="batch-invoice-pagemessage-body-message">
              <FormattedMessage
                id="BATCH_PAYMENT_LIMIT_MESSAGE"
                defaultMessage="<bold>Payment limit for some invoices:</bold> Invoices over {amount} must be paid separately."
                values={{
                  bold: (chunks: any) => <span className="bold-text">{chunks}</span>,
                  amount: (
                    <FormattedNumber
                      value={maxSelectableBalance}
                      style="currency"
                      currency={currency}
                      minimumFractionDigits={2}
                      maximumFractionDigits={2}
                    />
                  ),
                }}
              />
            </div>
            <div
              className="batch-invoice-pagemessage-body-cancel-btn"
              onClick={handleClose}
              aria-label="Close batch invoice limit message"
            >
              <Close color={colors.gray02} />
            </div>
          </div>
        </div>
      </div>
      <style jsx>{`
        .batch-invoice-pagemessage-wrapper {
          margin-bottom: 16px;
        }
        .batch-invoice-pagemessage-container {
          width: 100%;
          padding: 16px;
          border-radius: 8px;
          border: 1px solid ${colors.lightBlue};
        }
        .batch-invoice-pagemessage-body {
          display: flex;
          align-items: flex-start;
          gap: 8px;
        }
        .batch-invoice-pagemessage-body-message {
          flex: 1;
          padding: 2px 0 2px 2px;
          font-family: AvenirNextforINTUIT-Regular;
          color: ${colors.gray};
          font-size: ${fontSize.xs};
          line-height: 20px;
          letter-spacing: 0%;
          font-style: normal;
        }
        .bold-text {
          font-family: AvenirNextforINTUIT-Demi;
          font-weight: 600;
        }
        .batch-invoice-pagemessage-body-cancel-btn {
          cursor: pointer;
        }
      `}</style>
    </React.Fragment>
  );
};

export default BatchInvoicePageMessage;
